import { useParams, useRouterState } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { SUPPORTED_LOCALES, type SupportedLocale } from "../../lib/i18n";

const LABELS: Record<SupportedLocale, string> = {
  en: "English",
  fr: "Français",
  ar: "العربية",
  es: "Español",
  it: "Italiano",
  de: "Deutsch",
  nl: "Nederlands",
};

/** Top-level routes with no `/$lang` version (see `src/routes/*.tsx`). */
const UNPREFIXED_PAGES = new Set(["account", "sign-in", "sign-up", "forgot-password", "reset-password"]);

/**
 * The same page in another language, with its query and hash intact.
 *
 * The query is the guest's context: stay dates, party, city, filters, the
 * package and time on a booking step, the rateKey on a hotel checkout. Every
 * language switch goes through here so none of it is dropped.
 *
 * The account and auth pages live outside `/$lang` and keep their URL:
 * prefixing them gives `/fr/account`, which does not exist. Any other
 * lang-less path gets the prefix, as the layout's own redirect would.
 */
export function switchLocale(href: string, nextLang: SupportedLocale): string {
  const [pathAndSearch, hash = ""] = href.split("#", 2);
  const [pathname, search = ""] = pathAndSearch.split("?", 2);
  const parts = pathname.split("/");

  if (SUPPORTED_LOCALES.includes(parts[1] as SupportedLocale)) {
    parts[1] = nextLang;
  } else if (UNPREFIXED_PAGES.has(parts[1] ?? "")) {
    return href;
  } else if (pathname === "" || pathname === "/") {
    parts.splice(1, 1, nextLang);
  } else {
    parts.splice(1, 0, nextLang);
  }

  const nextPath = parts.join("/") || `/${nextLang}`;
  return `${nextPath}${search ? `?${search}` : ""}${hash ? `#${hash}` : ""}`;
}

export function LanguageSwitcher() {
  const { t } = useTranslation();
  const { lang } = (useParams({ strict: false }) as unknown) as { lang?: SupportedLocale };
  const location = useRouterState({ select: (state) => state.location });
  const current = lang ?? "en";
  const search = typeof location.search === "string" ? location.search : "";
  const hash = location.hash ? `#${location.hash}` : "";
  const href = location.href ?? `${location.pathname}${location.searchStr ?? search}${hash}`;

  return (
    <nav
      aria-label={t("common.language", { defaultValue: "Language" })}
      className="flex items-center gap-1"
    >
      {SUPPORTED_LOCALES.map((locale) => (
        <a
          key={locale}
          href={switchLocale(href, locale)}
          lang={locale}
          dir={locale === "ar" ? "rtl" : "ltr"}
          aria-current={locale === current ? "page" : undefined}
          className={
            locale === current
              ? "rounded-full bg-[#110E39] navy-depth px-3 py-1.5 text-[12px] font-bold text-white transition"
              : "rounded-full px-3 py-1.5 text-[12px] font-bold text-[#525466] transition hover:bg-white hover:text-[#110E39]"
          }
        >
          {LABELS[locale]}
        </a>
      ))}
    </nav>
  );
}
